import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { 
  ArrowLeft,
  Users, 
  Trophy, 
  MapPin,
  Building,
  Shield,
  Calendar,
  Clock,
  Award,
  Target,
  Star,
  ChevronRight,
  TrendingUp,
  Activity,
  BarChart3,
  Percent,
  User,
  Hash
} from 'lucide-react';
import { teamsApi, Team } from '../../services/api';
import { useSiteConfig } from '../../hooks/useSiteConfig';
import Breadcrumb from '../Common/Breadcrumb';

const TeamDetail: React.FC = () => {
  const { slug, year } = useParams<{ slug: string; year?: string }>();
  const siteConfig = useSiteConfig();
  const [activeTab, setActiveTab] = useState<'overview' | 'roster' | 'stats' | 'schedule'>('overview');
  const currentYear = year ? parseInt(year) : 2024; // Default to 2024 season

  // Fetch team details
  const { data: team, isLoading, error } = useQuery<Team>({
    queryKey: ['team', slug],
    queryFn: async () => {
      const response = await teamsApi.getBySlug(slug!);
      return response;
    },
    enabled: !!slug,
  });

  // Fetch team championships
  const { data: championships = [] } = useQuery({
    queryKey: ['team-championships', slug],
    queryFn: async () => {
      const response = await teamsApi.getChampionships(slug!);
      console.log('Championships API response:', response);
      return response.data || [];
    },
    enabled: !!slug,
  });

  // Fetch team roster
  const { data: roster = [] } = useQuery({
    queryKey: ['team-roster', slug, currentYear],
    queryFn: async () => {
      const response = await teamsApi.getRoster(slug!, currentYear);
      return response.data || [];
    },
    enabled: !!slug,
  });

  // Fetch team schedule
  const { data: schedule = [] } = useQuery({
    queryKey: ['team-schedule', slug, currentYear],
    queryFn: async () => {
      const response = await teamsApi.getSchedule(slug!, currentYear);
      return response.data || [];
    },
    enabled: !!slug,
  });

  // Fetch team seasons
  const { data: seasons = [] } = useQuery({
    queryKey: ['team-seasons', slug],
    queryFn: async () => {
      const response = await teamsApi.getSeasons(slug!);
      return response.data || [];
    },
    enabled: !!slug,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (error || !team) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="glass-card p-12 text-center max-w-md">
          <Trophy className="w-16 h-16 text-error mx-auto mb-4 opacity-50" />
          <h1 className="text-2xl font-bold text-white mb-2">Team Not Found</h1>
          <p className="text-gray-400 mb-6">
            {error ? (error as Error).message : 'The team you are looking for does not exist.'}
          </p>
          <Link
            to="/teams"
            className="btn-primary inline-flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Teams</span>
          </Link>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Shield },
    { id: 'roster', label: 'Roster', icon: Users },
    { id: 'stats', label: 'Stats', icon: BarChart3 },
    { id: 'schedule', label: 'Schedule', icon: Calendar },
  ];

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          {/* Breadcrumb Navigation */}
          <Breadcrumb
            items={[
              { label: 'All Teams', path: '/teams' },
              { label: team?.name || 'Team Details' },
              ...(year ? [{ label: `${currentYear}-${(currentYear + 1).toString().slice(2)} Season`, isActive: true }] : [])
            ]}
          />

          <div className="glass-card p-8">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
              <div className="flex items-center space-x-6">
                {/* Team Logo Placeholder */}
                <div className="relative">
                  <div className="absolute inset-0 bg-primary/20 blur-2xl" />
                  <div className="relative w-24 h-24 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/30 flex items-center justify-center">
                    <span className="text-3xl font-black text-primary">
                      {team.abbreviation || team.name.substring(0, 3).toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Team Info */}
                <div>
                  <Link 
                    to={`/teams/${team.slug}`}
                    className="block"
                  >
                    <h1 className="text-4xl font-black text-white mb-2 hover:text-primary transition-colors cursor-pointer">
                      {team.name}
                    </h1>
                  </Link>
                  <div className="flex items-center space-x-4">
                    {team.sport?.name && (
                      <span className="badge badge-primary">
                        {team.sport.name}
                      </span>
                    )}
                    {team.conference?.name && (
                      <span className="text-gray-400">
                        {team.conference.name}
                      </span>
                    )}
                    {team.division?.name && (
                      <span className="text-gray-400">
                        {team.division.name}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="flex space-x-6">
                {team.founded_year && (
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary">
                      {team.founded_year}
                    </div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider">
                      Founded
                    </div>
                  </div>
                )}
                {championships && (
                  <div className="text-center">
                    <div className="text-3xl font-bold text-secondary">
                      {championships.length}
                    </div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider">
                      Championships
                    </div>
                  </div>
                )}
                {championships && championships.length > 0 && (
                  <div className="text-center">
                    <div className="text-3xl font-bold text-accent">
                      {Math.max(...championships.map((c: any) => c.year || 0)) + 1}
                    </div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider">
                      Last Title
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Season Navigation */}
        {seasons && seasons.length > 0 && (
          <div className="glass-card p-6 mb-8">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-primary" />
              Available Seasons
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
              {seasons.map((season: any) => {
                const isCurrentSeason = season.start_year === currentYear;
                return (
                  <Link
                    key={season.id}
                    to={`/teams/${slug}/${season.start_year}`}
                    className={`
                      p-3 rounded-lg border transition-all duration-200 text-center
                      ${isCurrentSeason 
                        ? 'bg-primary/20 border-primary/50 text-primary' 
                        : 'bg-surface/30 border-border text-gray-300 hover:border-primary/30 hover:bg-surface/50'
                      }
                    `}
                  >
                    <div className="font-semibold">
                      {season.start_year}-{(season.start_year + 1).toString().slice(2)}
                    </div>
                    <div className="text-xs mt-1">
                      {season.championship_won && '🏆 '}
                      {season.name.replace(`${season.start_year}-`, '').replace(`${season.start_year}`, '')}
                    </div>
                  </Link>
                );
              })}
            </div>
            {!year && (
              <div className="mt-4 p-4 rounded-lg bg-secondary/10 border border-secondary/30">
                <p className="text-secondary text-sm text-center">
                  💡 Select a season above to view detailed season information, schedule, and roster
                </p>
              </div>
            )}
          </div>
        )}

        {/* Tabs - Only show if a specific season is selected */}
        {year && (
          <div className="glass-card p-1 mb-8">
            <div className="flex space-x-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`
                      flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg
                      transition-all duration-200
                      ${activeTab === tab.id
                        ? 'bg-primary/20 text-primary border border-primary/30'
                        : 'text-gray-400 hover:text-white hover:bg-surface/50'
                      }
                    `}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Tab Content - Only show if a specific season is selected */}
        {year && (
          <AnimatePresence mode="wait">
            {activeTab === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="grid grid-cols-1 lg:grid-cols-3 gap-8"
            >
              {/* Team Information */}
              <div className="lg:col-span-2 space-y-6">
                <div className="glass-card p-6">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                    <Shield className="w-5 h-5 mr-2 text-primary" />
                    Team Information
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                        Location
                      </div>
                      <div className="text-white">
                        {team.city}, {team.state || team.country || 'USA'}
                      </div>
                    </div>
                    {team.home_venue?.name && (
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                          Home Stadium
                        </div>
                        <div className="text-white">{team.home_venue.name}</div>
                      </div>
                    )}
                    {team.conference?.name && (
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                          Conference
                        </div>
                        <div className="text-white">{team.conference.name}</div>
                      </div>
                    )}
                    {team.division?.name && (
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                          Division
                        </div>
                        <div className="text-white">{team.division.name}</div>
                      </div>
                    )}
                    {team.founded_year && (
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                          Team History
                        </div>
                        <div className="text-white font-semibold">Since {team.founded_year}</div>
                      </div>
                    )}
                    {team.last_championship_year && (
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                          Last Championship
                        </div>
                        <div className="text-white font-semibold">
                          {team.last_championship_year}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Championships History */}
                {championships && championships.length > 0 && (
                  <div className="glass-card p-6">
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                      <Trophy className="w-5 h-5 mr-2 text-secondary" />
                      Championship History
                    </h2>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 rounded-lg bg-surface/50 border border-secondary/20">
                        <div className="flex items-center space-x-3">
                          <Award className="w-8 h-8 text-secondary" />
                          <div>
                            <div className="text-white font-semibold">
                              Total Championships
                            </div>
                            <div className="text-sm text-gray-400">
                              League titles won
                            </div>
                          </div>
                        </div>
                        <div className="text-3xl font-bold text-secondary">
                          {championships.length}
                        </div>
                      </div>
                      
                      {/* List of championships */}
                      <div className="space-y-2">
                        <div className="text-sm text-gray-400 uppercase tracking-wider mb-2">
                          Championship Years
                        </div>
                        {championships.slice(0, 5).map((champ: any) => (
                          <Link
                            key={champ.id}
                            to={`/teams/${team.slug}/${champ.year}`}
                            className="block p-3 rounded-lg bg-surface/30 border border-border hover:border-primary/50 hover:bg-surface/50 transition-all duration-200 cursor-pointer group"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <Trophy className="w-4 h-4 text-secondary group-hover:text-primary transition-colors" />
                                <div>
                                  <div className="text-white font-medium group-hover:text-primary transition-colors">
                                    {champ.year + 1} {champ.championship_name}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    Coach: {champ.coach} • Record: {champ.record}
                                  </div>
                                </div>
                              </div>
                              <div className="text-sm text-gray-400 group-hover:text-primary transition-colors">
                                {champ.season_name}
                              </div>
                            </div>
                          </Link>
                        ))}
                        {championships.length > 5 && (
                          <div className="text-sm text-gray-400 text-center pt-2">
                            +{championships.length - 5} more championships
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Quick Facts */}
                <div className="glass-card p-6">
                  <h3 className="text-lg font-bold text-white mb-4">
                    Quick Facts
                  </h3>
                  <div className="space-y-3">
                    {team.abbreviation && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Abbreviation</span>
                        <span className="text-white font-semibold">
                          {team.abbreviation}
                        </span>
                      </div>
                    )}
                    {team.level?.name && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Level</span>
                        <span className="text-white font-semibold">
                          {team.level.name}
                        </span>
                      </div>
                    )}
                    {team.sport?.name && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Sport</span>
                        <span className="text-white font-semibold">
                          {team.sport.name}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Stadium Info */}
                {team.home_venue && (
                  <div className="glass-card p-6">
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                      <Building className="w-5 h-5 mr-2 text-primary" />
                      Home Venue
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <div className="text-sm text-gray-400 mb-1">Stadium Name</div>
                        <div className="text-white font-semibold">
                          {team.home_venue.name}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-400 mb-1">Location</div>
                        <div className="text-white">
                          {team.home_venue.city}, {team.home_venue.state || team.home_venue.country || 'USA'}
                        </div>
                      </div>
                      {team.home_venue.capacity && (
                        <div>
                          <div className="text-sm text-gray-400 mb-1">Capacity</div>
                          <div className="text-white font-semibold">
                            {team.home_venue.capacity.toLocaleString()}
                          </div>
                        </div>
                      )}
                      {team.home_venue.opened_year && (
                        <div>
                          <div className="text-sm text-gray-400 mb-1">Opened</div>
                          <div className="text-white font-semibold">
                            {team.home_venue.opened_year}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'roster' && (
            <motion.div
              key="roster"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-6"
            >
              <div className="glass-card p-6">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                  <Users className="w-5 h-5 mr-2 text-primary" />
                  Current Roster
                </h2>
                {roster && roster.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider pb-3">#</th>
                          <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider pb-3">Name</th>
                          <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider pb-3">Position</th>
                          <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider pb-3">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {roster.map((player: any) => (
                          <tr key={player.id} className="hover:bg-surface/30 transition-colors">
                            <td className="py-3 text-white font-semibold">
                              {player.jersey_number}
                            </td>
                            <td className="py-3">
                              <div>
                                <Link
                                  to={`/players/${player.player?.slug || player.slug || player.player?.id || player.id}`}
                                  className="text-white font-medium hover:text-primary transition-colors cursor-pointer"
                                >
                                  {player.player?.person?.first_name} {player.player?.person?.last_name}
                                </Link>
                                {player.is_captain && (
                                  <span className="text-xs text-secondary ml-2">Captain</span>
                                )}
                              </div>
                            </td>
                            <td className="py-3 text-gray-300">
                              {player.player?.primary_position_name || 'N/A'}
                            </td>
                            <td className="py-3">
                              <div className="flex items-center space-x-2">
                                {player.is_starter && (
                                  <span className="text-xs px-2 py-1 rounded-full bg-primary/20 text-primary">
                                    Starter
                                  </span>
                                )}
                                {player.player?.is_active && (
                                  <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400">
                                    Active
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">
                      No roster data available
                    </p>
                  </div>
                )}
              </div>

              {/* Season Selector */}
              {championships && championships.length > 0 && (
                <div className="glass-card p-6">
                  <h3 className="text-lg font-bold text-white mb-4">
                    Championship Seasons
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {championships.map((champ: any) => (
                      <Link
                        key={champ.id}
                        to={`/teams/${team.slug}/${champ.year}`}
                        className="p-3 rounded-lg bg-surface/30 border border-border hover:border-primary/50 hover:bg-surface/50 transition-all duration-200 cursor-pointer group"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-white font-medium group-hover:text-primary transition-colors">
                              {champ.season_name}
                            </div>
                            <div className="text-xs text-gray-400">
                              {champ.year + 1} Champions • {champ.record}
                            </div>
                          </div>
                          <Trophy className="w-4 h-4 text-secondary group-hover:text-primary transition-colors" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'stats' && (
            <motion.div
              key="stats"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6"
            >
              <div className="glass-card p-6">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2 text-primary" />
                  Team Statistics
                </h2>
                <div className="space-y-4">
                  <div className="p-3 rounded-lg bg-surface/30 border border-border">
                    <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">
                      All-Time Record
                    </div>
                    <div className="text-lg font-bold text-white">
                      Championship Wins: {championships?.length || 0}
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-surface/30 border border-border">
                    <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">
                      Years Active
                    </div>
                    <div className="text-lg font-bold text-white">
                      {team.founded_year} - Present ({new Date().getFullYear() - team.founded_year} years)
                    </div>
                  </div>
                </div>
              </div>

              <div className="glass-card p-6">
                <h3 className="text-lg font-bold text-white mb-4">
                  Season Performance
                </h3>
                <div className="text-center py-8">
                  <Target className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">
                    Detailed season statistics coming soon
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {activeTab === 'schedule' && (
            <motion.div
              key="schedule"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-6"
            >
              <div className="glass-card p-6">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                  <Calendar className="w-5 h-5 mr-2 text-primary" />
                  {currentYear}-{(currentYear + 1).toString().slice(2)} Season Schedule
                </h2>
                {schedule && schedule.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider pb-3">Week</th>
                          <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider pb-3">Date</th>
                          <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider pb-3">Opponent</th>
                          <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider pb-3">Location</th>
                          <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider pb-3">Result</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {schedule.map((game: any) => {
                          const isHome = game.home_team === team?.id;
                          const opponent = isHome ? game.away_team_name : game.home_team_name;
                          const opponentAbbr = isHome ? game.away_team_abbreviation : game.home_team_abbreviation;
                          const teamScore = isHome ? game.home_score : game.away_score;
                          const oppScore = isHome ? game.away_score : game.home_score;
                          const isWin = game.status === 'completed' && teamScore > oppScore;
                          const isLoss = game.status === 'completed' && teamScore < oppScore;
                          
                          return (
                            <tr key={game.id} className="hover:bg-surface/30 transition-colors">
                              <td className="py-3 text-white">
                                {game.week || '-'}
                              </td>
                              <td className="py-3 text-gray-300">
                                {new Date(game.game_date).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric' 
                                })}
                              </td>
                              <td className="py-3">
                                <div className="flex items-center space-x-2">
                                  <span className="text-gray-400">
                                    {isHome ? 'vs' : '@'}
                                  </span>
                                  <span className="text-white font-medium">
                                    {opponent}
                                  </span>
                                  <span className="text-gray-500 text-sm">
                                    ({opponentAbbr})
                                  </span>
                                </div>
                              </td>
                              <td className="py-3 text-gray-300">
                                {game.venue_name}
                              </td>
                              <td className="py-3">
                                {game.status === 'completed' ? (
                                  <div className="flex items-center space-x-2">
                                    <span className={`font-semibold ${isWin ? 'text-green-400' : isLoss ? 'text-red-400' : 'text-yellow-400'}`}>
                                      {isWin ? 'W' : isLoss ? 'L' : 'T'}
                                    </span>
                                    <span className="text-white">
                                      {teamScore}-{oppScore}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-gray-400 text-sm">
                                    {game.status === 'scheduled' ? 'Upcoming' : game.status}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">
                      No schedule data available
                    </p>
                  </div>
                )}
              </div>

              <div className="glass-card p-6">
                <h3 className="text-lg font-bold text-white mb-4">
                  Recent Games
                </h3>
                <div className="text-center py-6">
                  <Clock className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">
                    Game results will appear here when available
                  </p>
                </div>
              </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}

        {/* Comprehensive team overview when no specific season is selected */}
        {!year && (
          <div className="space-y-8">
            {/* Team Overview Cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-1 lg:grid-cols-3 gap-8"
            >
              {/* Team Information */}
              <div className="lg:col-span-2 space-y-6">
                <div className="glass-card p-6">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                    <Shield className="w-5 h-5 mr-2 text-primary" />
                    Team Overview
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                        Location
                      </div>
                      <div className="text-white">
                        {team.city}, {team.state || team.country || 'USA'}
                      </div>
                    </div>
                    {team.home_venue?.name && (
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                          Home Stadium
                        </div>
                        <div className="text-white">{team.home_venue.name}</div>
                      </div>
                    )}
                    {team.conference?.name && (
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                          Conference
                        </div>
                        <div className="text-white">{team.conference.name}</div>
                      </div>
                    )}
                    {team.division?.name && (
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                          Division
                        </div>
                        <div className="text-white">{team.division.name}</div>
                      </div>
                    )}
                    {team.founded_year && (
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                          Founded
                        </div>
                        <div className="text-white font-semibold">{team.founded_year}</div>
                      </div>
                    )}
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                        Years Active
                      </div>
                      <div className="text-white font-semibold">
                        {team.founded_year ? new Date().getFullYear() - team.founded_year : 'N/A'} years
                      </div>
                    </div>
                  </div>
                </div>

                {/* All-Time Championships */}
                {championships && championships.length > 0 && (
                  <div className="glass-card p-6">
                    <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                      <Trophy className="w-5 h-5 mr-2 text-secondary" />
                      Championship History
                    </h2>
                    
                    {/* Championship Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div className="text-center p-4 rounded-lg bg-surface/50 border border-secondary/20">
                        <div className="text-3xl font-bold text-secondary mb-1">
                          {championships.length}
                        </div>
                        <div className="text-sm text-gray-400">Total Championships</div>
                      </div>
                      <div className="text-center p-4 rounded-lg bg-surface/50 border border-primary/20">
                        <div className="text-3xl font-bold text-primary mb-1">
                          {championships.length > 0 ? Math.min(...championships.map((c: any) => c.year)) + 1 : 'N/A'}
                        </div>
                        <div className="text-sm text-gray-400">First Championship</div>
                      </div>
                      <div className="text-center p-4 rounded-lg bg-surface/50 border border-accent/20">
                        <div className="text-3xl font-bold text-accent mb-1">
                          {championships.length > 0 ? Math.max(...championships.map((c: any) => c.year)) + 1 : 'N/A'}
                        </div>
                        <div className="text-sm text-gray-400">Most Recent</div>
                      </div>
                    </div>
                    
                    {/* Championship List */}
                    <div className="space-y-2">
                      <div className="text-sm text-gray-400 uppercase tracking-wider mb-2">
                        All Championships
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {championships.map((champ: any) => (
                          <Link
                            key={champ.id}
                            to={`/teams/${team.slug}/${champ.year}`}
                            className="p-3 rounded-lg bg-surface/30 border border-border hover:border-primary/50 hover:bg-surface/50 transition-all duration-200 cursor-pointer group"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <Trophy className="w-4 h-4 text-secondary group-hover:text-primary transition-colors" />
                                <div>
                                  <div className="text-white font-medium group-hover:text-primary transition-colors">
                                    {champ.year + 1} Champions
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    Record: {champ.record}
                                  </div>
                                </div>
                              </div>
                              <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-primary transition-colors" />
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Recent Performance Summary */}
                <div className="glass-card p-6">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2 text-primary" />
                    Franchise Summary
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <div className="text-sm text-gray-400 mb-2">Franchise Highlights</div>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-300">Championships Won</span>
                            <span className="text-secondary font-semibold">{championships?.length || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-300">Years in League</span>
                            <span className="text-white font-semibold">
                              {team.founded_year ? new Date().getFullYear() - team.founded_year : 'N/A'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-300">Available Seasons</span>
                            <span className="text-primary font-semibold">{seasons?.length || 0}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <div className="text-sm text-gray-400 mb-2">Organization</div>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-300">Sport</span>
                            <span className="text-white font-semibold">{team.sport?.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-300">Conference</span>
                            <span className="text-white font-semibold">{team.conference?.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-300">Division</span>
                            <span className="text-white font-semibold">{team.division?.name}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Quick Facts */}
                <div className="glass-card p-6">
                  <h3 className="text-lg font-bold text-white mb-4">
                    Quick Facts
                  </h3>
                  <div className="space-y-3">
                    {team.abbreviation && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Abbreviation</span>
                        <span className="text-white font-semibold">
                          {team.abbreviation}
                        </span>
                      </div>
                    )}
                    {team.level?.name && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Level</span>
                        <span className="text-white font-semibold">
                          {team.level.name}
                        </span>
                      </div>
                    )}
                    {team.sport?.name && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Sport</span>
                        <span className="text-white font-semibold">
                          {team.sport.name}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Stadium Info */}
                {team.home_venue && (
                  <div className="glass-card p-6">
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                      <Building className="w-5 h-5 mr-2 text-primary" />
                      Home Venue
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <div className="text-sm text-gray-400 mb-1">Stadium Name</div>
                        <div className="text-white font-semibold">
                          {team.home_venue.name}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-400 mb-1">Location</div>
                        <div className="text-white">
                          {team.home_venue.city}, {team.home_venue.state || team.home_venue.country || 'USA'}
                        </div>
                      </div>
                      {team.home_venue.capacity && (
                        <div>
                          <div className="text-sm text-gray-400 mb-1">Capacity</div>
                          <div className="text-white font-semibold">
                            {team.home_venue.capacity.toLocaleString()}
                          </div>
                        </div>
                      )}
                      {team.home_venue.opened_year && (
                        <div>
                          <div className="text-sm text-gray-400 mb-1">Opened</div>
                          <div className="text-white font-semibold">
                            {team.home_venue.opened_year}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Season Navigation Hint */}
                <div className="glass-card p-6">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                    <Calendar className="w-5 h-5 mr-2 text-primary" />
                    Explore Seasons
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Select a season above to view detailed roster, schedule, stats, and game results.
                  </p>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary mb-1">
                      {seasons?.length || 0}
                    </div>
                    <div className="text-xs text-gray-400">Available Seasons</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamDetail;